import { BellIcon } from '@/components/icons/bell-icon';
import { LongArrowIcon } from '@/components/icons/long-arrow-icon';
import Button from '@/components/ui/button';
import Logo from '@/components/ui/logo';
import { useModalAction } from '@/components/ui/modal/modal.context';
import { drawerAtom } from '@/store/drawer-atom';
import { Attachment } from '@/types';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import { Image } from '@/components/ui/image';
import { twMerge } from 'tailwind-merge';

interface MaintenanceModeProps {
  data: {
    newsLetterTitle?: string;
    newsLetterDescription?: string;
    aboutUsTitle?: string;
    aboutUsDescription?: string;
    contactUsTitle?: string;
    contactDetails?: {
      [key: string]: any;
    };
    title: string;
    description: string;
    buttonTitleOne?: string;
    buttonTitleTwo?: string;
    isOverlayColor?: string;
    overlayColorRange?: string;
    overlayColor?: string;
    image: Attachment;
  };
  renderCountDown: React.ReactNode;
  className?: string;
  showLogo?: boolean;
}

const MaintenanceMode = ({
  data,
  renderCountDown,
  className,
  showLogo = true,
}: MaintenanceModeProps) => {
  const { openModal } = useModalAction();
  const [_, setDrawerView] = useAtom(drawerAtom);

  const openNewsLetterModal = () => {
    openModal('NEWSLETTER_MODAL', {
      title: data?.newsLetterTitle as string,
      description: data?.newsLetterDescription as string,
    });
  };

  const handleSidebar = (view: string) => {
    setDrawerView({
      display: true,
      view,
      data: {
        aboutUsTitle: data?.aboutUsTitle,
        aboutUsDescription: data?.aboutUsDescription,
        contactUsTitle: data?.contactUsTitle,
        contactDetails: data?.contactDetails,
      },
    });
  };

  return (
    <div
      className={twMerge(
        classNames(
          'relative h-screen min-h-[43.75rem] w-full bg-[#e6e5eb] text-center',
          className,
        ),
      )}
    >
      <div className="relative z-20 mx-auto h-[calc(100%-70px)] max-w-7xl px-8">
        {showLogo ? (
          <div className="flex items-center justify-center pt-8">
            <Logo />
          </div>
        ) : (
          ''
        )}
        <div className="relative mt-8 lg:mt-16">
          <div>
            {data?.title ? (
              <h1 className="mb-4 text-xl font-bold tracking-tight text-black lg:mb-8 lg:text-6xl">
                {data?.title}
              </h1>
            ) : (
              ''
            )}
            {data?.description ? (
              <p className="m-0 mx-auto max-w-xl text-base leading-8 text-black lg:text-lg">
                {data?.description}
              </p>
            ) : (
              ''
            )}
            {renderCountDown}
            {data?.buttonTitleOne || data?.buttonTitleTwo ? (
              <div className="mt-8 flex flex-wrap items-center justify-center gap-5 lg:mt-16">
                {data?.buttonTitleOne ? (
                  <Button
                    onClick={openNewsLetterModal}
                    className="notify-button group h-auto rounded-full py-2.5 text-sm md:text-base"
                  >
                    {data?.buttonTitleOne}
                    <span className="notify-button-icon flex h-9 w-9 rounded-full bg-accent-hover transition-colors duration-500 group-hover:bg-accent ltr:ml-3 rtl:mr-3">
                      <BellIcon className="m-auto" />
                    </span>
                  </Button>
                ) : (
                  ''
                )}
                {data?.buttonTitleTwo ? (
                  <Button
                    onClick={() => handleSidebar('MAINTENANCE_MORE_INFO')}
                    className="info-button group h-auto rounded-full bg-white py-2.5 text-sm text-slate-700 hover:bg-slate-300 hover:text-slate-600 md:text-base"
                  >
                    {data?.buttonTitleTwo}
                    <span className="info-button-icon flex h-9 w-9 rounded-full bg-slate-300 text-black duration-500 group-hover:bg-slate-400 group-hover:text-white ltr:ml-3 rtl:mr-3 rtl:rotate-180 rtl:transform">
                      <LongArrowIcon className="m-auto text-3xl" />
                    </span>
                  </Button>
                ) : (
                  ''
                )}
              </div>
            ) : (
              ''
            )}
          </div>
        </div>
      </div>
      {data?.image?.original ? (
        <div className="absolute top-0 left-0 z-10 h-full w-full bg-no-repeat">
          <Image
            src={data?.image?.original}
            alt="maintenance image"
            fill
            className="object-contain object-bottom"
            unoptimized={true}
          />
        </div>
      ) : (
        ''
      )}
      {data?.isOverlayColor ? (
        <div
          style={{
            backgroundColor: data?.overlayColor as string,
            opacity: data?.overlayColorRange as string,
          }}
          className="absolute top-0 left-0 z-10 h-full w-full"
        />
      ) : (
        ''
      )}
    </div>
  );
};

export default MaintenanceMode;
